import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Box, Typography, Button, TextField, Divider, Chip, Avatar } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import axios from "axios";

const pulse = keyframes`0%{transform:scale(1)}50%{transform:scale(1.03)}100%{transform:scale(1)}`;
const blink = keyframes`0%{opacity:0}50%{opacity:1}100%{opacity:0}`;
const LiveDot = styled("span")({ height:8, width:8, backgroundColor:"#4CAF50",
  borderRadius:"50%", display:"inline-block", marginRight:6,
  animation:`${blink} 1.5s ease-in-out infinite` });

const JoinBtn = styled(Button)({
  background: "linear-gradient(45deg,#FF6B6B,#FF2BAA)",
  borderRadius: "50px", color: "#fff", padding: "16px 40px",
  fontWeight: 900, fontSize: "1.1rem", textTransform: "uppercase",
  letterSpacing: 1, boxShadow: "0 6px 24px rgba(255,65,150,0.4)",
  animation: `${pulse} 2s ease-in-out infinite`,
  "&:hover": { filter: "brightness(1.1)", animation: "none" },
});

const STEPS = [
  { icon: "📧", label: "Enter your email" },
  { icon: "🔒", label: "Choose a password" },
  { icon: "🎉", label: "Watch for free — forever!" },
];

const SignupPage = () => {
  const location   = useLocation();
  const params     = new URLSearchParams(location.search);
  const pname      = params.get("pname") || "";
  const [performer, setPerformer] = useState(null);
  const [liveCount, setLiveCount] = useState(843);

  // Fetch performer preview if pname provided
  useEffect(() => {
    if (!pname) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/performers`, { params: { name: pname, limit: 1 } })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (list[0]) {
          const a = list[0]["@attributes"] || {};
          setPerformer({
            name: a.Name,
            thumb: list[0]?.Media?.Pic?.Full?.["@attributes"]?.Src || list[0]?.Media?.Pic?.Thumb?.["@attributes"]?.Src || "",
            age: a.Age, ethnicity: a.Ethnicity,
          });
        }
      }).catch(() => {});
    // Randomize live count for social proof
    setLiveCount(800 + Math.floor(Math.random() * 200));
  }, [pname]);

  // Update title
  useEffect(() => {
    document.title = performer
      ? `Watch ${performer.name} Live — Join Free | Instacamgirls`
      : "Join Free — Watch Live Cam Girls | Instacamgirls";
  }, [performer]);

  const signupUrl = `https://instacamgirlslive.com/signup?AFNO=1-94033${pname ? `&pname=${pname}` : ""}`;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0a0a0a", display: "flex", flexDirection: "column", alignItems: "center" }}>

      {/* Header */}
      <Box sx={{ width: "100%", bgcolor: "#111", borderBottom: "1px solid #222", px: 3, py: 1.5,
        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 900, fontSize: "1.2rem", color: "#fff",
          background: "linear-gradient(90deg,#FF6B6B,#FF2BAA)", WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent" }}>
          InstaCamGirls
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <LiveDot /><Typography sx={{ color: "#4CAF50", fontSize: "0.8rem", fontWeight: 600 }}>
            {liveCount} girls live
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 480, width: "100%", px: 2, py: 4 }}>
        {/* Performer preview */}
        {performer && (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{ position: "relative", display: "inline-block", mb: 1.5 }}>
              <Avatar src={performer.thumb} sx={{
                width: 100, height: 100, mx: "auto",
                border: "3px solid #FF4196",
                boxShadow: "0 0 20px rgba(255,65,150,0.5)",
              }} />
              <Box sx={{ position: "absolute", bottom: 4, right: 4, width: 18, height: 18,
                bgcolor: "#4CAF50", borderRadius: "50%", border: "2px solid #0a0a0a" }} />
            </Box>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.1rem" }}>
              {performer.name} is live right now! 🔴
            </Typography>
            <Typography sx={{ color: "#aaa", fontSize: "0.85rem", mt: 0.3 }}>
              {performer.age && `${performer.age} yrs`}{performer.ethnicity && ` · ${performer.ethnicity}`}
            </Typography>
          </Box>
        )}

        {!performer && (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: "#fff", lineHeight: 1.2, mb: 1 }}>
              Watch Live Cam Girls<br />
              <Box component="span" sx={{ background: "linear-gradient(90deg,#FF6B6B,#FF2BAA)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                For Free
              </Box>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
              <LiveDot />
              <Typography sx={{ color: "#4CAF50", fontWeight: 600 }}>{liveCount} girls streaming right now</Typography>
            </Box>
          </Box>
        )}

        {/* 3 steps */}
        <Box sx={{ bgcolor: "#161616", borderRadius: 2, p: 2.5, mb: 3 }}>
          <Typography sx={{ color: "#aaa", fontSize: "0.8rem", fontWeight: 700,
            letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>
            It's free, fast and secure
          </Typography>
          {STEPS.map((s, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: i < 2 ? 1.5 : 0 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "50%",
                background: "linear-gradient(135deg,#FF6B6B,#FF2BAA)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem", flexShrink: 0 }}>
                {s.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: "#fff", fontSize: "0.88rem", fontWeight: 600 }}>
                  Step {i + 1}: {s.label}
                </Typography>
              </Box>
              <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 20 }} />
            </Box>
          ))}
        </Box>

        {/* CTA */}
        <JoinBtn fullWidth onClick={() => window.location.href = signupUrl}>
          🔥 Join Free — Watch Now
        </JoinBtn>

        <Typography sx={{ color: "#555", fontSize: "0.75rem", textAlign: "center", mt: 1.5 }}>
          No credit card required • 100% free to browse • Cancel anytime
        </Typography>

        <Divider sx={{ my: 3, borderColor: "#222" }} />

        {/* Trust signals */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
          {["🔒 SSL Secured", "✅ 18+ Verified", "🌍 300k+ Members", "⭐ 4.8/5 Rating"].map(t => (
            <Typography key={t} sx={{ color: "#555", fontSize: "0.75rem" }}>{t}</Typography>
          ))}
        </Box>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button component={Link} to="/" sx={{ color: "#555", fontSize: "0.75rem" }}>
            ← Back to browsing
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SignupPage;
